<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';

import type { TpgChangeEvent, TpgChangeEventType } from '@nora-monorepo/grammar-ui';
import {
  GrammarLocalLoader,
  GrammarModal,
  GrammarTabBar,
  GrammarTabItem,
} from '@nora-monorepo/grammar-ui';
import {
  AvatarImage,
  getInitials,
  HoverableUserName,
} from '@nora-monorepo/ui-shared';

import { useConversationStore } from '#nora-chat-ui/stores/conversation/conversation-store';
import { useReactionsModalStore } from '#nora-chat-ui/stores/conversation/message/reactions-modal-store';
import { aggregateReactions } from '#nora-chat-ui/utils/aggregate-reactions';

const conversationStore = useConversationStore();
const reactionModalStore = useReactionsModalStore();
const {
  isOpen,
  messageId,
  conversationId,
  reactions,
  isLoading,
} = storeToRefs(reactionModalStore);
const selectedTab = ref('all');

const messageState = computed(() => {
  if (!messageId.value || !conversationId.value) {
    return undefined;
  }

  return conversationStore.getConversationMessage(
    conversationId.value,
    messageId.value,
  );
});

const totalReactions = computed(() => {
  let total = 0;
  messageState.value?.message.reactions?.forEach((reaction) => {
    total += reaction.count;
  });
  return total;
});

const reactionCounts = computed(() => aggregateReactions(reactions.value));

const visibleReactions = computed(() => selectedTab.value === 'all'
  ? reactions.value
  : reactions.value.filter(reaction => reaction.reaction === selectedTab.value));

watch(isOpen, (open) => {
  if (open) {
    selectedTab.value = 'all';
  }
});

function onTabChange(tab: TpgChangeEvent<TpgChangeEventType>) {
  if (typeof tab.value === 'string') {
    selectedTab.value = tab.value;
  }
}
</script>

<template>
  <GrammarModal
    :is-open="isOpen"
    :label="`Reactions (${totalReactions})`"
    @close="reactionModalStore.reset"
  >
    <template #content>
      <div v-if="isOpen" class="my-2 w-[min(80vw,28rem)] max-h-[min(60vh,28rem)] overflow-y-auto overflow-x-hidden overscroll-contain">
        <GrammarLocalLoader v-if="isLoading" />
        <template v-else>
          <GrammarTabBar
            :selected-value="selectedTab"
            class="px-5"
            justify-content="left"
            @change="onTabChange"
          >
            <GrammarTabItem :label="`All ${totalReactions}`" value="all" />
            <GrammarTabItem
              v-for="{ reaction, count } in reactionCounts"
              :key="reaction"
              :label="`${reaction} ${count}`"
              :value="reaction"
            />
          </GrammarTabBar>
          <div class="mt-4 space-y-2 pb-6">
            <div
              v-for="(reaction, index) in visibleReactions"
              :key="`${reaction.user.id}-${reaction.reaction}-${index}`"
              class="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-sm transition-colors"
            >
              <AvatarImage :id="reaction.user.id" :text="getInitials(reaction.user)" />
              <div class="flex-1 min-w-0">
                <HoverableUserName :user="reaction.user" />
              </div>
              <div class="shrink-0 text-2xl">
                {{ reaction.reaction }}
              </div>
            </div>
          </div>
        </template>
      </div>
    </template>
  </GrammarModal>
</template>
